/**
 * Shared user query columns - SINGLE SOURCE OF TRUTH
 * Used by both server-side (getData.ts) and client-side (swr.ts) queries
 * to ensure consistent data shape across the application.
 * 
 * IMPORTANT: Keep these as single-line strings to avoid whitespace issues with Supabase
 */

// Base user fields - always fetched
const USER_BASE_FIELDS = 'id, uuid, email, phone, first_name, last_name, avatar_url, role, app_mode, created_at, updated_at, id_verified_at, terms_accepted_at, privacy_policy_accepted_at, provider, provider_id, loan_promise, loan_promise_expires_at, loan_contact_accepted_at, loan_contact_denied_at, source, address_street, address_zip, address_city, address_country, broker_id';

// Broker relation - basic broker info
const USER_BROKER_RELATION = 'broker:broker_id(id, name, email, phone, title)';

// User images relation
const USER_IMAGES_RELATION = 'user_images(id, extension)';

// Interests relation - for tracking user interest in homes
const USER_INTERESTS_RELATION = 'interests(id, home_id, interest_level)';

// User roles relation
const USER_ROLES_RELATION = 'user_roles(role)';

// Searches relation (just IDs for counting)
// Note: searches table uses owner_id (not user_id) to reference users, so we need the foreign key hint
const USER_SEARCHES_BASIC_RELATION = 'searches!searches_new_owner_id_fkey(id)';

/**
 * Default user query for layout/context - minimal data for:
 * - AppProvider context (basic user info)
 * - Tracking identification
 * - Role checks
 * - Menu navigation (counts)
 */
export const getUserDataQuery = () => 
  `${USER_BASE_FIELDS}, ${USER_BROKER_RELATION}, ${USER_IMAGES_RELATION}, ${USER_SEARCHES_BASIC_RELATION}, ${USER_INTERESTS_RELATION}, ${USER_ROLES_RELATION}`;

/**
 * Lightweight user query for SWR/client-side - only data needed for UI state updates
 * Used by useUserData hook for real-time button state updates (wishlist, interests)
 * 
 * IMPORTANT: Keep this lightweight! Only fetch what's needed for:
 * - Interest button states (need home_id and interest_level from interests)
 * - Wishlist button states (checked via added_to_wishlist interest level)
 * - Basic user info for checks
 * 
 * Do NOT add nested home data here - it makes the query extremely slow!
 */
export const getUserDataFullQuery = () => 
  `${USER_BASE_FIELDS}, ${USER_BROKER_RELATION}, ${USER_SEARCHES_BASIC_RELATION}, ${USER_INTERESTS_RELATION}`;
